import { useCallback, useContext, useEffect, useState } from "react";
import { FilesSectionProps } from "./types";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { UserDetailsContext } from "../../contexts";
import { getAllProjectFiles } from "../../services/projectService";
import { ProjectFile as ProjectFileClass } from "../../proto/api_pb";
import { GetProjectFilesResponse } from "../../proto/api_pb";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { CircularProgress, IconButton, Typography } from "@mui/material";
import { Box, Checkbox } from "@mui/material";
import { projectPageStyles } from "../../pages/Projects/styles";
import { ProjectFile } from "../SingleProject/ProjectFile";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import { useLocation, useParams } from "react-router-dom";
import Previewer from "../Modal/Previewer";
const label = { inputProps: { "aria-label": "Checkbox demo" } };

export const FilesSection: React.FC<FilesSectionProps> = ({
  session,
  setInbox,
  isAllFilesSelected,
  setIsAllFilesSelected,
  setCheckedFiles,
  isFileChecked,
  handleCheckedFileChange,
  projectQueryValue,
  filter,
  filesSortBy,
  filesSortingIsAscending,
  setFilesSortingIsAscending,
  folderId,
  foldersLength,
  isAddFolderToProject,
  isFilesUpdated,
  setIsFilesUpdated,
  setIsCountUpdated,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [projectFiles, setProjectFiles] = useState<ProjectFileClass[]>([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);
  const [selectedFileToPreview, setSelectedFileToPreview] =
    useState<ProjectFileClass | null>(null);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { enterprise } = useContext(UserDetailsContext);
  const safeFoldersLength = foldersLength || 0;
  const { page, scrollContainerRef, setIsScrollEnabled, setPage } =
    useInfiniteScroll();
  const { id } = useParams();
  const location = useLocation();
  const { projectId, folderIdFromNavigation } = location.state || {};
  const isAddToProjectPage =
    (window.location.pathname.includes("my-tracks") &&
      folderIdFromNavigation) ||
    projectId;
  const isHubPage =
    window.location.pathname.includes("my-tracks") && !isAddToProjectPage;
  const isProjectorFolderOpen = window.location.pathname.includes("project");

  useEffect(() => {
    setPage(0);
    setProjectFiles([]);
    setIsLoading(true);
  }, [
    projectQueryValue,
    filter,
    filesSortBy,
    filesSortingIsAscending,
    setPage,
  ]);

  const getProjectFiles = useCallback(async () => {
    const token = await getValidAccessToken();
    const res = await getAllProjectFiles(
      token,
      id === "undefined" ? "" : (id ?? ""),
      projectQueryValue,
      filter,
      enterprise?.id as string,
      page,
      10,
      !isHubPage || !id ? false : true,
      filesSortBy,
      filesSortingIsAscending,
      folderId
    );
    if (res instanceof GetProjectFilesResponse) {
      setProjectFiles((prev) => {
        const existingFileIds = new Set(prev.map((file) => file.getId()));
        const newFiles = res
          .getProjectFilesList()
          .filter((file) => !existingFileIds.has(file.getId()));
        return [...prev, ...newFiles];
      });
      if (res.getProjectFilesList().length < 10) {
        setIsScrollEnabled(false);
        setIsLoading(false);
      } else {
        setIsScrollEnabled(true);
      }
    }
  }, [
    enterprise?.id,
    filesSortBy,
    filesSortingIsAscending,
    filter,
    getValidAccessToken,
    id,
    isHubPage,
    page,
    projectQueryValue,
    setIsScrollEnabled,
    folderId,
  ]);

  useEffect(() => {
    getProjectFiles();
  }, [getProjectFiles]);

  useEffect(() => {
    if (isFilesUpdated) {
      setPage(0);
      setProjectFiles([]);
      setIsLoading(true);
      getProjectFiles().then(() => {
        setIsFilesUpdated(false);
      });
      if (setIsCountUpdated) setIsCountUpdated(true);
    }
  }, [
    setPage,
    setProjectFiles,
    setIsLoading,
    getProjectFiles,
    isFilesUpdated,
    setIsFilesUpdated,
    setIsCountUpdated,
  ]);

const getPreviewType = (
  mimeType: string
): "image" | "video" | "pdf" | "text" | "other" => {
  if (!mimeType) return "other";

  const normalized = mimeType.trim().toLowerCase();

  // Known image types and common malformed/extension-only forms
  const imageTypes = [
    "image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp",
    "image/bmp", "image/svg+xml", "image/tiff", "image/heic", "image/heif",
    ".jpeg", "jpeg", ";jpeg", ".jpg", "jpg", ";jpg", ".png", "png", ";png",
    ".gif", "gif", ";gif", ".webp", "webp", ";webp", ".bmp", "bmp", ";bmp",
    ".svg", "svg", ";svg", ".tiff", "tiff", ";tiff", ".heic", "heic", ";heic",
    ".heif", "heif", ";heif"
  ];

  if (imageTypes.includes(normalized)) return "image";
  if (normalized.startsWith("image/")) return "image";

  if (normalized.startsWith("video/")) return "video";
  if (normalized === "application/pdf") return "pdf";
  if (normalized.startsWith("text/")) return "text";

  return "other";
};


  return (
    <>
      <Previewer
        fileUrl={selectedFileToPreview?.getFileUrl() ?? ""}
        fileType={getPreviewType(selectedFileToPreview?.getType() ?? "")}
        isPreviewModalOpen={isPreviewModalOpen}
        toggle={() => setIsPreviewModalOpen(!isPreviewModalOpen)}
      />
      <Box sx={projectPageStyles.filesScrollContainer}>
        <Box
          ref={scrollContainerRef}
          sx={{
            ...projectPageStyles.filesSectionContainer,
            height: `calc(100vh - 225px - ${
              safeFoldersLength === 0 ? 0 : safeFoldersLength < 8 ? 60 : 110
            }px)`,
          }}
        >
          <Box sx={projectPageStyles.titlesContainer}>
            <Box
              sx={{
                width: "400px",
              }}
            >
              {((!isAddFolderToProject && isAddToProjectPage) ||
                isHubPage ||
                isProjectorFolderOpen) && (
                <Checkbox
                  size="small"
                  style={{ marginRight: "18px" }}
                  checked={isAllFilesSelected}
                  {...label}
                  onChange={() => {
                    if (isAllFilesSelected) setCheckedFiles([]);
                    else setCheckedFiles(projectFiles);
                    setIsAllFilesSelected(!isAllFilesSelected);
                  }}
                />
              )}
              Name
              {filesSortBy === 0 && (
                <IconButton
                  sx={{ margin: "0px", padding: "0px" }}
                  onClick={() => {
                    setFilesSortingIsAscending(!filesSortingIsAscending);
                  }}
                >
                  {filesSortingIsAscending ? (
                    <ArrowUpwardIcon sx={projectPageStyles.arrowIcon} />
                  ) : (
                    <ArrowDownwardIcon sx={projectPageStyles.arrowIcon} />
                  )}
                </IconButton>
              )}
            </Box>
            <Box sx={{ width: "100px" }}>Type</Box>
            <Box sx={{ width: "100px" }}>Created</Box>
            <Box sx={{ width: "100px" }}>
              Updated
              {filesSortBy === 1 && (
                <IconButton
                  sx={{ margin: "0px", padding: "0px" }}
                  onClick={() => {
                    setFilesSortingIsAscending(!filesSortingIsAscending);
                  }}
                >
                  {filesSortingIsAscending ? (
                    <ArrowUpwardIcon sx={projectPageStyles.arrowIcon} />
                  ) : (
                    <ArrowDownwardIcon sx={projectPageStyles.arrowIcon} />
                  )}
                </IconButton>
              )}
            </Box>
            <Box sx={{ width: "100px" }}>
              Size
              {filesSortBy === 2 && (
                <IconButton
                  sx={{ margin: "0px", padding: "0px" }}
                  onClick={() => {
                    setFilesSortingIsAscending(!filesSortingIsAscending);
                  }}
                >
                  {filesSortingIsAscending ? (
                    <ArrowUpwardIcon sx={projectPageStyles.arrowIcon} />
                  ) : (
                    <ArrowDownwardIcon sx={projectPageStyles.arrowIcon} />
                  )}
                </IconButton>
              )}
            </Box>
            <Box sx={{ width: "200px" }}>Actions</Box>
          </Box>
          {projectFiles.map((projectFile, i) => {
            return (
              <ProjectFile
                projectFile={projectFile}
                key={i}
                session={session}
                setInbox={setInbox}
                getProjectFiles={getProjectFiles}
                isFileChecked={isFileChecked}
                handleCheckedFileChange={handleCheckedFileChange}
                setProjectFiles={setProjectFiles}
                isAddFolderToProject={isAddFolderToProject}
                allowDownload={true}
                setIsPreviewModalOpen={setIsPreviewModalOpen}
                setSelectedFileToPreview={setSelectedFileToPreview}
              />
            );
          })}
          {projectFiles.length === 0 && !isLoading && (
            <Box sx={projectPageStyles.emptyMessageContainer}>
              <Typography>No files found!</Typography>
            </Box>
          )}
          {isLoading && (
            <Box sx={projectPageStyles.loadingContainer}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
